import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, getDocs, getDoc, Timestamp, doc } from 'firebase/firestore';
import { Calendar, Clock, User, MapPin, CheckSquare, ArrowLeft, DollarSign, TrendingUp, TrendingDown, Minus, AlertCircle, BarChart3, Target, Zap, Users, FileText, Plus, Settings, Eye } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import PersonalDayView from './PersonalDayView';
import '../Dashboard.css';

// Fonction pour formater une adresse (objet ou chaîne)
const formatAddress = (address) => {
  if (typeof address === 'string') return address;
  if (typeof address === 'object' && address !== null) {
    const { street, city, postalCode, country } = address;
    const parts = [];
    if (street) parts.push(street);
    if (postalCode) parts.push(postalCode);
    if (city) parts.push(city);
    if (country) parts.push(country);
    return parts.join(', ');
  }
  return 'Adresse non spécifiée';
};

// Composant MetricWidget défini au niveau module pour éviter la réinstanciation
const MetricWidget = ({ title, value, subtitle, icon: Icon, color = "blue", progress = null }) => (
  <div className={`bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border-l-4 border-${color}-500 hover:shadow-md transition-all duration-200`}>
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center space-x-2">
        <Icon size={20} className={`text-${color}-500`} />
        <h3 className="font-semibold text-gray-700 dark:text-gray-200 text-sm">{title}</h3>
      </div>
      {progress !== null && (
        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
          progress >= 80 ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
          progress >= 60 ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
          'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
        }`}>
          {progress}%
        </span>
      )}
    </div>
    <p className="text-2xl font-bold text-gray-800 dark:text-white mb-1">{value}</p>
    {subtitle && <p className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>}
    {progress !== null && (
      <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
        <div
          className={`h-1.5 rounded-full transition-all duration-700 ${
            progress >= 80 ? 'bg-green-500' : progress >= 60 ? 'bg-yellow-500' : 'bg-red-500'
          }`}
          style={{ width: `${Math.min(100, progress)}%` }}
        />
      </div>
    )}
  </div>
);

const OverviewPage = ({ db, currentUser }) => {
  const [appointments, setAppointments] = useState([]);
  const [nextAppointment, setNextAppointment] = useState(null);
  const [stats, setStats] = useState({ weekCount: 0, monthCount: 0 });
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [financialData, setFinancialData] = useState({
    monthlyRevenue: 0,
    pendingPayments: 0,
    hourlyRate: 0,
    monthlyGoal: 0
  });
  const [revenueChartData, setRevenueChartData] = useState([]);
  const [planningData, setPlanningData] = useState({
    weeklyLoad: 0,
    availableSlots: 0,
    conflicts: 0,
    conflictDetails: []
  });
  const [nextMonthForecast, setNextMonthForecast] = useState({
    estimatedRevenue: 0,
    appointmentCount: 0,
    variation: 0,
    totalHours: 0,
    daysWithAppointments: 0,
    dailyRevenue: {},
    appointmentDetails: []
  });
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/dashboard');
  };

  // Fonctions de calcul des données (gardées identiques)
  const calculateFinancialData = useCallback(async (appointments) => {
    if (!appointments.length) return;
    
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    
    try {
      const contractsSnapshot = await getDocs(collection(db, 'contracts'));
      const contractsMap = {};
      contractsSnapshot.docs.forEach(doc => {
        contractsMap[doc.id] = doc.data();
      });

      const invoicesSnapshot = await getDocs(collection(db, 'invoices'));
      const pendingInvoices = invoicesSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(invoice => invoice.status === 'pending' || invoice.status === 'sent');

      const monthlyAppointments = appointments.filter(appt => 
        appt.date >= startOfMonth && appt.date <= endOfMonth
      );

      let totalRevenue = 0;
      let totalHours = 0;
      let hourlyRates = [];

      monthlyAppointments.forEach(appt => {
        const [startHours, startMinutes] = appt.startTime.split(':');
        const [endHours, endMinutes] = appt.endTime.split(':');
        const duration = (parseInt(endHours) * 60 + parseInt(endMinutes)) - (parseInt(startHours) * 60 + parseInt(startMinutes));
        const durationInHours = duration / 60;

        const clientContract = contractsMap[appt.clientId];
        const hourlyRate = clientContract?.hourlyRate || 0;
        
        totalRevenue += durationInHours * hourlyRate;
        totalHours += durationInHours;
        if (hourlyRate > 0) {
          hourlyRates.push(hourlyRate);
        }
      });

      let avgHourlyRate = 0;
      if (hourlyRates.length > 0) {
        const sum = hourlyRates.reduce((sum, rate) => sum + rate, 0);
        avgHourlyRate = Math.round(sum / hourlyRates.length);
      } else {
        avgHourlyRate = 35;
      }

      const pendingPayments = pendingInvoices.reduce((total, invoice) => {
        return total + (invoice.amount || 0);
      }, 0);

      const monthlyGoal = 3000;

      setFinancialData({
        monthlyRevenue: Math.round(totalRevenue),
        pendingPayments: Math.round(pendingPayments),
        hourlyRate: avgHourlyRate,
        monthlyGoal: monthlyGoal
      });

    } catch (error) {
      console.error("Erreur lors du calcul des données financières:", error);
      const monthlyAppointments = appointments.filter(appt => 
        appt.date >= startOfMonth && appt.date <= endOfMonth
      );
      
      const estimatedRevenue = monthlyAppointments.reduce((total, appt) => {
        const [startHours, startMinutes] = appt.startTime.split(':');
        const [endHours, endMinutes] = appt.endTime.split(':');
        const duration = (parseInt(endHours) * 60 + parseInt(endMinutes)) - (parseInt(startHours) * 60 + parseInt(startMinutes));
        return total + (duration / 60) * 35;
      }, 0);
      
      setFinancialData({
        monthlyRevenue: Math.round(estimatedRevenue),
        pendingPayments: Math.round(estimatedRevenue * 0.2),
        hourlyRate: 35,
        monthlyGoal: 2500
      });
    }
  }, [db]);

  const calculatePlanningData = useCallback((appointments) => {
    if (!appointments.length) return;
    
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(endOfWeek.getDate() + 7);
    
    const weeklyAppointments = appointments.filter(appt => 
      appt.date >= startOfWeek && appt.date < endOfWeek
    );
    
    const weeklyHours = weeklyAppointments.reduce((total, appt) => {
      const [startHours, startMinutes] = appt.startTime.split(':');
      const [endHours, endMinutes] = appt.endTime.split(':');
      const duration = (parseInt(endHours) * 60 + parseInt(endMinutes)) - (parseInt(startHours) * 60 + parseInt(startMinutes));
      return total + (duration / 60);
    }, 0);
    
    const conflictDetails = [];
    weeklyAppointments.forEach(appt => {
      const sameDayAppointments = weeklyAppointments.filter(a => 
        a.date.toDateString() === appt.date.toDateString() && a.id !== appt.id
      );
      
      sameDayAppointments.forEach(otherAppt => {
        if (otherAppt.startTime < appt.endTime && otherAppt.endTime > appt.startTime) {
          const conflict = {
            date: appt.date,
            appointment1: {
              id: appt.id,
              clientName: appt.clientName,
              startTime: appt.startTime,
              endTime: appt.endTime
            },
            appointment2: {
              id: otherAppt.id,
              clientName: otherAppt.clientName,
              startTime: otherAppt.startTime,
              endTime: otherAppt.endTime
            },
            overlapStart: appt.startTime < otherAppt.startTime ? otherAppt.startTime : appt.startTime,
            overlapEnd: appt.endTime < otherAppt.endTime ? appt.endTime : otherAppt.endTime
          };
          
          const conflictExists = conflictDetails.some(existingConflict => 
            (existingConflict.appointment1.id === conflict.appointment1.id && 
             existingConflict.appointment2.id === conflict.appointment2.id) ||
            (existingConflict.appointment1.id === conflict.appointment2.id && 
             existingConflict.appointment2.id === conflict.appointment1.id)
          );
          
          if (!conflictExists) {
            conflictDetails.push(conflict);
          }
        }
      });
    });
    
    setPlanningData({
      weeklyLoad: Math.round(weeklyHours),
      availableSlots: Math.max(0, 35 - weeklyHours),
      conflicts: conflictDetails.length,
      conflictDetails: conflictDetails
    });
  }, []);

  const generateRevenueChartData = useCallback(async () => {
    try {
      const now = new Date();
      const currentYear = now.getFullYear();
      const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
      
      const appointmentsRef = collection(db, 'appointments');
      const startDate = new Date(currentYear - 2, 0, 1);
      const endDate = new Date(currentYear, 11, 31, 23, 59, 59);
      
      const appointmentsQuery = query(
        appointmentsRef,
        where('employeeId', '==', currentUser.id || currentUser.uid),
        where('date', '>=', Timestamp.fromDate(startDate)),
        where('date', '<=', Timestamp.fromDate(endDate)),
        orderBy('date', 'asc')
      );

      const appointmentsSnapshot = await getDocs(appointmentsQuery);
      const allAppointments = appointmentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date.toDate()
      }));

      const contractsSnapshot = await getDocs(collection(db, 'contracts'));
      const contractsMap = {};
      contractsSnapshot.docs.forEach(doc => {
        contractsMap[doc.id] = doc.data();
      });

      const revenueByMonthYear = {};
      
      allAppointments.forEach(appt => {
        const apptDate = appt.date;
        const year = apptDate.getFullYear();
        const month = apptDate.getMonth();
        
        const [startHours, startMinutes] = appt.startTime.split(':');
        const [endHours, endMinutes] = appt.endTime.split(':');
        const duration = (parseInt(endHours) * 60 + parseInt(endMinutes)) - (parseInt(startHours) * 60 + parseInt(startMinutes));
        const durationInHours = duration / 60;

        const clientContract = contractsMap[appt.clientId];
        const hourlyRate = clientContract?.hourlyRate || financialData.hourlyRate || 35;
        const appointmentRevenue = durationInHours * hourlyRate;
        
        const key = `${year}-${month}`;
        if (!revenueByMonthYear[key]) {
          revenueByMonthYear[key] = 0;
        }
        revenueByMonthYear[key] += appointmentRevenue;
      });

      const chartData = [];
      
      for (let month = 0; month < 12; month++) {
        const monthData = { mois: monthNames[month] };
        
        for (let yearOffset = 2; yearOffset >= 0; yearOffset--) {
          const year = currentYear - yearOffset;
          const key = `${year}-${month}`;
          const revenue = revenueByMonthYear[key] || 0;
          monthData[year] = Math.round(revenue);
        }
        
        chartData.push(monthData);
      }

      setRevenueChartData(chartData);
    } catch (error) {
      console.error("Erreur lors de la génération des données du graphique:", error);
      const currentYear = new Date().getFullYear();
      const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
      const chartData = monthNames.map((monthName, monthIndex) => ({
        mois: monthName,
        [currentYear - 2]: Math.round(financialData.monthlyRevenue * 0.7 * (0.8 + Math.random() * 0.4)),
        [currentYear - 1]: Math.round(financialData.monthlyRevenue * 0.85 * (0.8 + Math.random() * 0.4)),
        [currentYear]: Math.round(financialData.monthlyRevenue * (0.8 + Math.random() * 0.4))
      }));
      setRevenueChartData(chartData);
    }
  }, [db, currentUser, financialData.monthlyRevenue, financialData.hourlyRate]);

  const calculateNextMonthForecast = useCallback(async () => {
    try {
      const now = new Date();
      const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      const startOfNextMonth = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 1);
      const endOfNextMonth = new Date(nextMonth.getFullYear(), nextMonth.getMonth() + 1, 0, 23, 59, 59);

      const appointmentsRef = collection(db, 'appointments');
      const nextMonthQuery = query(
        appointmentsRef,
        where('employeeId', '==', currentUser.id || currentUser.uid),
        where('date', '>=', Timestamp.fromDate(startOfNextMonth)),
        where('date', '<=', Timestamp.fromDate(endOfNextMonth)),
        orderBy('date', 'asc')
      );

      const nextMonthSnapshot = await getDocs(nextMonthQuery);
      const nextMonthAppointments = nextMonthSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date.toDate()
      }));

      const contractsSnapshot = await getDocs(collection(db, 'contracts'));
      const contractsMap = {};
      contractsSnapshot.docs.forEach(doc => {
        contractsMap[doc.id] = doc.data();
      });

      const clientsSnapshot = await getDocs(collection(db, 'clients'));
      const clientsMap = {};
      clientsSnapshot.docs.forEach(doc => {
        clientsMap[doc.id] = doc.data();
      });

      let estimatedRevenue = 0;
      let totalHours = 0;
      const dailyRevenue = {};
      const appointmentDetails = [];

      nextMonthAppointments.forEach(appt => {
        const [startHours, startMinutes] = appt.startTime.split(':');
        const [endHours, endMinutes] = appt.endTime.split(':');
        const duration = (parseInt(endHours) * 60 + parseInt(endMinutes)) - (parseInt(startHours) * 60 + parseInt(startMinutes));
        const durationInHours = duration / 60;

        const clientContract = contractsMap[appt.clientId];
        const hourlyRate = clientContract?.hourlyRate || financialData.hourlyRate || 35;
        const appointmentRevenue = durationInHours * hourlyRate;
        
        estimatedRevenue += appointmentRevenue;
        totalHours += durationInHours;

        const dayKey = appt.date.toISOString().split('T')[0];
        if (!dailyRevenue[dayKey]) {
          dailyRevenue[dayKey] = 0;
        }
        dailyRevenue[dayKey] += appointmentRevenue;

        const clientData = clientsMap[appt.clientId];
        const clientName = clientData ? `${clientData.lastName} ${clientData.firstName}` : 'Client inconnu';
        
        appointmentDetails.push({
          date: appt.date,
          clientName: clientName,
          startTime: appt.startTime,
          endTime: appt.endTime,
          duration: durationInHours,
          hourlyRate: hourlyRate,
          revenue: appointmentRevenue
        });
      });

      appointmentDetails.sort((a, b) => a.date - b.date);

      const variation = financialData.monthlyRevenue > 0 
        ? Math.round(((estimatedRevenue - financialData.monthlyRevenue) / financialData.monthlyRevenue) * 100)
        : 0;

      const daysWithAppointments = Object.keys(dailyRevenue).length;

      setNextMonthForecast({
        estimatedRevenue: Math.round(estimatedRevenue),
        appointmentCount: nextMonthAppointments.length,
        variation: variation,
        totalHours: Math.round(totalHours * 10) / 10,
        daysWithAppointments: daysWithAppointments,
        dailyRevenue: dailyRevenue,
        appointmentDetails: appointmentDetails
      });

    } catch (error) {
      console.error("Erreur lors du calcul des prévisions:", error);
      setNextMonthForecast({
        estimatedRevenue: 0,
        appointmentCount: 0,
        variation: 0,
        totalHours: 0,
        daysWithAppointments: 0,
        dailyRevenue: {},
        appointmentDetails: []
      });
    }
  }, [db, currentUser, financialData.monthlyRevenue, financialData.hourlyRate]);

  useEffect(() => {
    if (financialData.monthlyRevenue > 0) {
      generateRevenueChartData();
      calculateNextMonthForecast();
    }
  }, [financialData.monthlyRevenue, generateRevenueChartData, calculateNextMonthForecast]);

  useEffect(() => {
    const fetchData = async () => {
      if (!db || !currentUser) return;

      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);

      const appointmentsRef = collection(db, 'appointments');
      const appointmentsQuery = query(
        appointmentsRef,
        where('employeeId', '==', currentUser.id || currentUser.uid),
        where('date', '>=', Timestamp.fromDate(startOfMonth)),
        where('date', '<=', Timestamp.fromDate(endOfMonth)),
        orderBy('date', 'asc'),
        orderBy('startTime', 'asc')
      );

      try {
        const appointmentsSnapshot = await getDocs(appointmentsQuery);
        const fetchedAppointments = await Promise.all(appointmentsSnapshot.docs.map(async (docSnapshot) => {
          const appointmentData = docSnapshot.data();
          const appointmentDate = appointmentData.date.toDate();
          
          let clientName = "Client inconnu";
          let clientAddress = "Adresse inconnue";

          if (appointmentData.clientId) {
            const clientDocRef = doc(db, 'clients', appointmentData.clientId);
            const clientDocSnapshot = await getDoc(clientDocRef);
            if (clientDocSnapshot.exists()) {
              const clientData = clientDocSnapshot.data();
              clientName = `${clientData.lastName} ${clientData.firstName}`;
              // Récupérer l'adresse complète (objet ou string)
              clientAddress = clientData.address || "Adresse inconnue";
            }
          }

          return {
            id: docSnapshot.id,
            ...appointmentData,
            date: appointmentDate,
            clientName,
            clientAddress
          };
        }));

        // Tri des rendez-vous par ordre chronologique
        const sortedAppointments = fetchedAppointments.sort((a, b) => {
          const dateA = new Date(a.date.setHours(0,0,0,0));
          const dateB = new Date(b.date.setHours(0,0,0,0));
          if (dateA.getTime() !== dateB.getTime()) {
            return dateA.getTime() - dateB.getTime();
          }
          return a.startTime.localeCompare(b.startTime);
        });

        setAppointments(sortedAppointments);

        // Set next appointment
        const upcomingAppointments = sortedAppointments.filter(appt => 
          appt.date > now || (appt.date.toDateString() === now.toDateString() && appt.startTime > now.toTimeString().slice(0, 5))
        );
        if (upcomingAppointments.length > 0) {
          setNextAppointment(upcomingAppointments[0]);
        }

        // Calculate stats
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        startOfWeek.setHours(0, 0, 0, 0);

        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(endOfWeek.getDate() + 7);

        const weekCount = sortedAppointments.filter(appt => 
          appt.date >= startOfWeek && appt.date < endOfWeek
        ).length;

        const monthCount = sortedAppointments.filter(appt => 
          appt.date >= startOfMonth && appt.date <= endOfMonth
        ).length;

        setStats({ weekCount, monthCount });

        // Calcul des données financières et planning
        calculateFinancialData(sortedAppointments);
        calculatePlanningData(sortedAppointments);

      } catch (error) {
        console.error("Erreur lors de la récupération des rendez-vous:", error);
      }
    };

    fetchData();
  }, [db, currentUser, calculateFinancialData, calculatePlanningData]);

  const formatDate = (date) => {
    return date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const formatTime = (time) => {
    return time;
  };

  const calculateTimeRemaining = (appointment) => {
    if (!appointment) return '';
    const now = new Date();
    const appointmentDateTime = new Date(appointment.date);
    const [hours, minutes] = appointment.startTime.split(':');
    appointmentDateTime.setHours(parseInt(hours, 10), parseInt(minutes, 10));
    const diff = appointmentDateTime - now;
    const hours_remaining = Math.floor(diff / 3600000);
    const minutes_remaining = Math.floor((diff % 3600000) / 60000);
    return `${hours_remaining}h ${minutes_remaining}min`;
  };

  const getAppointmentsForDate = (date) => {
    const now = new Date();
    const startOfDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const endOfDay = new Date(startOfDay);
    endOfDay.setDate(endOfDay.getDate() + 1);
    
    return appointments.filter(appointment => {
      const appointmentDate = new Date(appointment.date);
      
      // Vérifier que le rendez-vous est exactement pour la date demandée
      return appointmentDate >= startOfDay && appointmentDate < endOfDay;
    }).sort((a, b) => a.startTime.localeCompare(b.startTime));
  };

  const todayAppointments = getAppointmentsForDate(new Date());
  const tomorrowDate = new Date();
  tomorrowDate.setDate(tomorrowDate.getDate() + 1);
  const tomorrowAppointments = getAppointmentsForDate(tomorrowDate);

  return (
    <div className="dashboard-container">
      {/* Header avec bouton de retour */}
      <div className="dashboard-header flex-row">
        <button
          onClick={handleBack}
          className="dashboard-button-secondary"
          aria-label="Retour au tableau de bord"
        >
          <ArrowLeft size={18} />
          <span>Retour</span>
        </button>
        <div className="text-center">
          <h1 className="dashboard-title">Vue d'ensemble</h1>
          {currentUser && (
            <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">
              {currentUser.firstName} {currentUser.lastName}
            </p>
          )}
        </div>
        <div></div> {/* Spacer pour centrer le titre */}
      </div>

      {/* Section Priorité - Métriques critiques */}
      <div className="dashboard-grid">
        {/* Prochain RDV */}
        {nextAppointment ? (
          <div className="dashboard-tile tile-col-span-1 bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0">
            <div className="flex items-center justify-between mb-2">
              <h3 className="tile-title text-white">
                <Clock className="tile-icon text-white" />
                Prochain RDV
              </h3>
              <span className="text-blue-100 text-sm font-bold">{formatTime(nextAppointment.startTime)}</span>
            </div>
            <p className="font-semibold text-lg mb-1 truncate">{nextAppointment.clientName}</p>
            <p className="text-blue-100 text-sm">Dans {calculateTimeRemaining(nextAppointment)}</p>
          </div>
        ) : (
          <div className="dashboard-tile tile-col-span-1 bg-gradient-to-br from-slate-500 to-slate-600 text-white border-0">
            <h3 className="tile-title text-white mb-2">
              <Clock className="tile-icon text-white" />
              Prochain RDV
            </h3>
            <p className="text-slate-200 text-sm">Aucun RDV à venir</p>
          </div>
        )}

        {/* Revenus du mois */}
        <div className="dashboard-tile tile-col-span-1 bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0">
          <h3 className="tile-title text-white mb-2">
            <DollarSign className="tile-icon text-white" />
            Revenus du mois
          </h3>
          <p className="text-2xl font-bold mb-1">{financialData.monthlyRevenue}€</p>
          <p className="text-green-100 text-sm mb-2">Objectif: {financialData.monthlyGoal}€</p>
          <div className="w-full bg-white/20 rounded-full h-1.5">
            <div
              className="bg-white h-1.5 rounded-full transition-all duration-700"
              style={{ width: `${Math.min(100, financialData.monthlyGoal > 0 ? (financialData.monthlyRevenue / financialData.monthlyGoal) * 100 : 0)}%` }}
            />
          </div>
        </div>

        {/* Charge hebdomadaire */}
        <div className="dashboard-tile tile-col-span-1 bg-gradient-to-br from-orange-500 to-amber-600 text-white border-0">
          <h3 className="tile-title text-white mb-2">
            <BarChart3 className="tile-icon text-white" />
            Charge hebdo
          </h3>
          <p className="text-2xl font-bold mb-1">{planningData.weeklyLoad}h</p>
          <p className="text-orange-100 text-sm mb-2">sur 35h</p>
          <div className="w-full bg-white/20 rounded-full h-1.5">
            <div
              className="bg-white h-1.5 rounded-full transition-all duration-700"
              style={{ width: `${Math.min(100, (planningData.weeklyLoad / 35) * 100)}%` }}
            />
          </div>
        </div>

        {/* Conflits */}
        <div className={`dashboard-tile tile-col-span-1 border-0 text-white ${planningData.conflicts > 0 ? 'bg-gradient-to-br from-red-500 to-red-600' : 'bg-gradient-to-br from-slate-500 to-slate-600'}`}>
          <h3 className="tile-title text-white mb-2">
            <AlertCircle className="tile-icon text-white" />
            Conflits
          </h3>
          <p className="text-2xl font-bold mb-1">{planningData.conflicts}</p>
          <p className={`text-sm ${planningData.conflicts > 0 ? 'text-red-100' : 'text-slate-200'}`}>
            {planningData.conflicts > 0 ? 'à résoudre' : 'Aucun conflit'}
          </p>
        </div>
      </div>

      {/* Grille principale */}
      <div className="dashboard-grid">

        {/* Rendez-vous du jour et demain */}
        <div className="dashboard-tile tile-col-span-3">
          <h2 className="tile-title">
            <Calendar className="tile-icon" />
            Rendez-vous à venir
          </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Aujourd'hui */}
              <div>
                <h3 className="font-semibold text-green-700 dark:text-green-400 mb-3 flex items-center">
                  <Clock size={16} className="mr-2" />
                  Aujourd'hui ({todayAppointments.length})
                </h3>
                {todayAppointments.length > 0 ? (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {todayAppointments.map(appointment => (
                      <div key={appointment.id} className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-200 dark:border-green-700/40">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-green-800 dark:text-green-300 text-sm">{appointment.clientName}</p>
                            <p className="text-green-600 dark:text-green-400 text-xs">{formatTime(appointment.startTime)} - {formatTime(appointment.endTime)}</p>
                            <p className="text-green-500 dark:text-green-500 text-xs mt-1">{formatAddress(appointment.clientAddress)}</p>
                          </div>
                          <span className="text-green-700 dark:text-green-300 text-xs font-medium bg-green-100 dark:bg-green-800/40 px-2 py-1 rounded">
                            {formatTime(appointment.startTime)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Aucun rendez-vous aujourd'hui</p>
                )}
              </div>

              {/* Demain */}
              <div>
                <h3 className="font-semibold text-blue-700 dark:text-blue-400 mb-3 flex items-center">
                  <Clock size={16} className="mr-2" />
                  Demain ({tomorrowAppointments.length})
                </h3>
                {tomorrowAppointments.length > 0 ? (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {tomorrowAppointments.map(appointment => (
                      <div key={appointment.id} className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-700/40">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-blue-800 dark:text-blue-300 text-sm">{appointment.clientName}</p>
                            <p className="text-blue-600 dark:text-blue-400 text-xs">{formatTime(appointment.startTime)} - {formatTime(appointment.endTime)}</p>
                            <p className="text-blue-500 dark:text-blue-500 text-xs mt-1">{formatAddress(appointment.clientAddress)}</p>
                          </div>
                          <span className="text-blue-700 dark:text-blue-300 text-xs font-medium bg-blue-100 dark:bg-blue-800/40 px-2 py-1 rounded">
                            {formatTime(appointment.startTime)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Aucun rendez-vous demain</p>
                )}
              </div>
            </div>
          </div>

        {/* Vue personnelle du planning du jour */}
        <div className="dashboard-tile tile-col-span-3">
          <h2 className="tile-title">
            <Calendar className="tile-icon" />
            Mon Planning du Jour
          </h2>
            <PersonalDayView 
              db={db}
              currentDate={new Date()}
              currentUser={currentUser}
              showCompletedAppointments={false}
            />
          </div>

        {/* Détails des conflits */}
        {planningData.conflictDetails.length > 0 && (
          <div className="dashboard-tile tile-col-span-3 bg-red-50 border-red-500">
            <h2 className="tile-title text-red-700">
              <AlertCircle className="tile-icon text-red-500" />
              Conflits détectés ({planningData.conflictDetails.length})
            </h2>
              <div className="space-y-4">
                {planningData.conflictDetails.map((conflict, index) => (
                  <div key={index} className="bg-white p-4 rounded-lg border border-red-300">
                    <p className="text-sm font-medium text-red-700 mb-3">
                      {formatDate(conflict.date)} - Chevauchenent: {conflict.overlapStart} à {conflict.overlapEnd}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div className="bg-red-100 p-3 rounded">
                        <p className="font-medium text-red-800">{conflict.appointment1.clientName}</p>
                        <p className="text-red-600">{conflict.appointment1.startTime} - {conflict.appointment1.endTime}</p>
                      </div>
                      <div className="bg-red-100 p-3 rounded">
                        <p className="font-medium text-red-800">{conflict.appointment2.clientName}</p>
                        <p className="text-red-600">{conflict.appointment2.startTime} - {conflict.appointment2.endTime}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* Performance financière */}
        <div className="dashboard-tile tile-col-span-2">
          <h2 className="tile-title">
            <DollarSign className="tile-icon text-green-500" />
            Performance financière
          </h2>
            <div className="space-y-4">
              <MetricWidget 
                title="Revenus du mois" 
                value={`${financialData.monthlyRevenue}€`} 
                subtitle={`Objectif: ${financialData.monthlyGoal}€`}
                icon={TrendingUp}
                color="green"
                progress={Math.min(100, (financialData.monthlyRevenue / financialData.monthlyGoal) * 100)}
              />
              <MetricWidget 
                title="Paiements en attente" 
                value={`${financialData.pendingPayments}€`} 
                subtitle="à encaisser"
                icon={FileText}
                color="orange"
              />
              <MetricWidget 
                title="Taux horaire moyen" 
                value={`${financialData.hourlyRate}€`} 
                subtitle="par heure"
                icon={Target}
                color="purple"
              />
            </div>
          </div>

        {/* Prévisions du mois prochain */}
        <div className="dashboard-tile tile-col-span-2">
          <h2 className="tile-title">
            <Eye className="tile-icon text-cyan-500" />
            Prévisions du mois prochain
          </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-semibold text-gray-800">Revenus estimés</p>
                  <p className="text-3xl font-bold text-cyan-600">{nextMonthForecast.estimatedRevenue}€</p>
                </div>
                <div className={`text-right ${nextMonthForecast.variation > 0 ? 'text-green-600' : nextMonthForecast.variation < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                  <p className="text-sm font-medium">
                    {nextMonthForecast.variation > 0 ? '+' : ''}{nextMonthForecast.variation}%
                  </p>
                  <p className="text-xs">vs ce mois</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-cyan-50 dark:bg-cyan-900/20 p-3 rounded-lg border border-cyan-100 dark:border-cyan-800/30">
                  <p className="text-sm font-medium text-cyan-800 dark:text-cyan-300">RDV planifiés</p>
                  <p className="text-xl font-bold text-cyan-600 dark:text-cyan-400">{nextMonthForecast.appointmentCount}</p>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-100 dark:border-blue-800/30">
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-300">Jours avec RDV</p>
                  <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{nextMonthForecast.daysWithAppointments}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-100 dark:border-green-800/30">
                  <p className="text-sm font-medium text-green-800 dark:text-green-300">Heures totales</p>
                  <p className="text-xl font-bold text-green-600 dark:text-green-400">{nextMonthForecast.totalHours}h</p>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg border border-purple-100 dark:border-purple-800/30">
                  <p className="text-sm font-medium text-purple-800 dark:text-purple-300">Tendance</p>
                  <p className={`text-sm font-bold flex items-center gap-1 ${nextMonthForecast.variation > 0 ? 'text-green-600 dark:text-green-400' : nextMonthForecast.variation < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'}`}>
                    {nextMonthForecast.variation > 0 ? (
                      <><TrendingUp size={14} /> Hausse</>
                    ) : nextMonthForecast.variation < 0 ? (
                      <><TrendingDown size={14} /> Baisse</>
                    ) : (
                      <><Minus size={14} /> Stable</>
                    )}
                  </p>
                </div>
              </div>
              
              {/* Détails des rendez-vous programmés */}
              {nextMonthForecast.appointmentDetails.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Rendez-vous programmés :</h4>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {nextMonthForecast.appointmentDetails.map((appt, index) => (
                      <div key={index} className="bg-gray-50 dark:bg-gray-800/50 p-2 rounded text-xs border border-gray-100 dark:border-gray-700/50">
                        <div className="flex justify-between">
                          <span className="font-medium text-gray-800 dark:text-gray-200">{formatDate(appt.date)}</span>
                          <span className="text-cyan-600 dark:text-cyan-400 font-bold">{Math.round(appt.revenue)}€</span>
                        </div>
                        <div className="text-gray-600 dark:text-gray-400">
                          {appt.clientName} - {appt.startTime} ({appt.duration}h × {appt.hourlyRate}€/h)
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {nextMonthForecast.appointmentCount > 0 ?
                  `Basé sur ${nextMonthForecast.appointmentCount} rendez-vous programmés sur ${nextMonthForecast.daysWithAppointments} jours` :
                  'Aucun rendez-vous programmé pour le mois prochain'
                }
              </div>
            </div>
          </div>

        {/* Statistiques rapides */}
        <div className="dashboard-tile tile-col-span-1">
          <h2 className="tile-title">
            <BarChart3 className="tile-icon text-purple-500" />
            Statistiques
          </h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-100 dark:border-purple-800/30">
                <p className="text-lg font-semibold text-purple-800 dark:text-purple-300">Cette semaine</p>
                <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">{stats.weekCount}</p>
                <p className="text-sm text-purple-500 dark:text-purple-400">rendez-vous</p>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800/30">
                <p className="text-lg font-semibold text-blue-800 dark:text-blue-300">Ce mois</p>
                <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{stats.monthCount}</p>
                <p className="text-sm text-blue-500 dark:text-blue-400">rendez-vous</p>
              </div>
            </div>
          </div>

        {/* Graphique des revenus mensuels avec comparaison des années */}
        <div className="dashboard-tile tile-col-span-4">
          <h2 className="tile-title">
            <TrendingUp className="tile-icon text-blue-500" />
            Comparatif des revenus
          </h2>
            <div className="h-48 sm:h-64 md:h-80">
              {revenueChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={revenueChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="mois" />
                    <YAxis />
                    <Tooltip
                      formatter={(value, name) => [`${value}€`, name]}
                      labelFormatter={(label) => `Mois: ${label}`}
                    />
                    <Legend />
                    {Object.keys(revenueChartData[0] || {})
                      .filter(key => key !== 'mois')
                      .sort()
                      .map((year, index) => (
                        <Bar
                          key={year}
                          dataKey={year}
                          name={year}
                          fill={index === 0 ? "#F59E0B" : index === 1 ? "#10B981" : "#3B82F6"}
                          radius={[4, 4, 0, 0]}
                        />
                      ))
                    }
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-500 dark:text-gray-400">Chargement des données...</p>
                </div>
              )}
            </div>
          </div>
      </div>

      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-4 right-4 bg-blue-500 text-white p-2 rounded-full shadow-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all duration-300 z-50"
          aria-label="Retour en haut"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default OverviewPage;
